import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://cync.threnlabs.com';

export default function useSEO({
    title,
    description,
    keywords,
    jsonLd,
    ogType = 'website',
    ogImage = 'https://cync.threnlabs.com/logo.png'
}) {
    const location = useLocation();
    const currentUrl = `${BASE_URL}${location.pathname}`;

    useEffect(() => {
        // 1. Update Title
        if (title) {
            document.title = title;
        }

        // Helper to get or create meta tag
        const setMetaTag = (name, content, isProperty = false) => {
            if (!content) return;
            const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                if (isProperty) {
                    el.setAttribute('property', name);
                } else {
                    el.setAttribute('name', name);
                }
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        // Helper to get or create link tag
        const setLinkTag = (rel, href) => {
            if (!href) return;
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        // 2. Set Meta Tags
        setMetaTag('description', description);
        setMetaTag('keywords', keywords);

        // Open Graph
        setMetaTag('og:title', title, true);
        setMetaTag('og:description', description, true);
        setMetaTag('og:url', currentUrl, true);
        setMetaTag('og:type', ogType, true);
        setMetaTag('og:image', ogImage, true);
        setMetaTag('og:site_name', 'ScholarsAnchor', true);

        // Twitter Card
        setMetaTag('twitter:card', 'summary_large_image');
        setMetaTag('twitter:title', title);
        setMetaTag('twitter:description', description);
        setMetaTag('twitter:image', ogImage);

        // 3. Set Canonical Link
        setLinkTag('canonical', currentUrl);

        // 4. Inject JSON-LD structured data
        let scriptEl = document.getElementById('json-ld-structured-data');
        if (scriptEl) {
            scriptEl.remove();
        }

        if (jsonLd) {
            scriptEl = document.createElement('script');
            scriptEl.id = 'json-ld-structured-data';
            scriptEl.type = 'application/ld+json';
            scriptEl.text = JSON.stringify(jsonLd);
            document.head.appendChild(scriptEl);
        }

        // Clean up JSON-LD script on unmount
        return () => {
            const el = document.getElementById('json-ld-structured-data');
            if (el) {
                el.remove();
            }
        };
    }, [title, description, keywords, currentUrl, jsonLd, ogType, ogImage]);
}
